import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Plus, Edit, Trash2, Search, Filter, FolderOpen } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useProjects } from '../context/ProjectContext';
import { useAuth } from '../context/AuthContext';
import { API_URL, apiRequest } from '../config/api';

const Projects = () => {
  const { t } = useTranslation();
  const [selectedRows, setSelectedRows] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { projects, addProject, updateProject, deleteProject, deleteProjects, setProjects } = useProjects();
  const { user, handleAuthError } = useAuth();
  const projectsRef = useRef([]);

  useEffect(() => {
    projectsRef.current = Array.isArray(projects) ? projects : [];
  }, [projects]);

  const [newProject, setNewProject] = useState({
    name: '',
    startDate: '',
    endDate: '',
    manager: '',
    emissions: 0
  });

  // Fetch projects from backend
  const fetchProjects = useCallback(async () => {
    if (!user?.token) {
      console.log('No authentication token available, skipping fetch');
      return;
    }

    const hasCached = Array.isArray(projectsRef.current) && projectsRef.current.length > 0;
    if (!hasCached) {
      setLoading(true);
    }
    setError(null);
    
    try {
      console.log('Fetching projects from backend...');
      const response = await apiRequest(`${API_URL}/api/projects`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${user.token}`,
          'Content-Type': 'application/json',
        },
      }, handleAuthError);

      console.log(`Fetch projects response status: ${response.status}`);

      if (!response.ok) {
        let errorMessage = 'Failed to fetch projects';
        try {
          const errorData = await response.json();
          errorMessage = errorData.message || errorData.error || errorMessage;
          console.error('Fetch projects error details:', errorData);
        } catch (parseError) {
          console.error('Failed to parse error response:', parseError);
          errorMessage = `HTTP ${response.status}: ${response.statusText}`;
        }
        throw new Error(errorMessage);
      }

      const fetchedProjects = await response.json();
      console.log('Projects fetched successfully:', fetchedProjects);
      
      // Update local state with fetched projects
      setProjects(fetchedProjects);
    } catch (error) {
      console.error('Error fetching projects:', error);
      setError(error.message);
    } finally {
      if (!hasCached) {
        setLoading(false);
      }
    }
  }, [user?.token, setProjects, handleAuthError]);

  // Load projects on component mount and when user changes
  useEffect(() => {
    fetchProjects();
  }, [user?.token, fetchProjects]);

  const handleCheckboxChange = (id) => {
    setSelectedRows(prev => 
      prev.includes(id) 
        ? prev.filter(rowId => rowId !== id)
        : [...prev, id]
    );
  };

  const handleSelectAll = () => {
    if (selectedRows.length === filteredProjects.length) {
      setSelectedRows([]);
    } else {
      setSelectedRows(filteredProjects.map(item => item.id));
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedRows.length === 0) return;
    
    const message = selectedRows.length === 1 
      ? t('projects.confirmations.deleteSingle')
      : t('projects.confirmations.deleteMultiple', { count: selectedRows.length });
    
    if (window.confirm(message)) {
      try {
        if (!user?.token) {
          console.error('No authentication token available');
          alert(t('projects.alerts.authRequiredDelete'));
          return;
        }

        // Delete projects one by one via API
        const deletePromises = selectedRows.map(async (projectId) => {
          console.log(`Attempting to delete project ${projectId} (bulk operation)...`);
          const response = await fetch(`${API_URL}/api/projects/${projectId}`, {
            method: 'DELETE',
            headers: {
              'Authorization': `Bearer ${user.token}`,
              'Content-Type': 'application/json',
            },
          });
          
          console.log(`Bulk delete response status for project ${projectId}: ${response.status}`);

                     if (!response.ok) {
             let errorMessage = `Failed to delete project ${projectId}`;
             try {
               const errorData = await response.json();
               errorMessage = `${errorMessage}: ${errorData.message || errorData.error || 'Unknown error'}`;
               console.error(`Delete project ${projectId} error details:`, errorData);
             } catch (parseError) {
               console.error(`Failed to parse error response for project ${projectId}:`, parseError);
               errorMessage = `${errorMessage}: HTTP ${response.status}: ${response.statusText}`;
             }
             throw new Error(errorMessage);
           }

          return projectId;
        });

        // Wait for all deletions to complete
        await Promise.all(deletePromises);
        
        console.log('Projects deleted successfully:', selectedRows);
        
        // Remove projects from local state
        deleteProjects(selectedRows);
        setSelectedRows([]);
      } catch (error) {
        console.error('Error deleting projects:', error);
        alert(`Failed to delete projects: ${error.message}`);
      }
    }
  };

  const handleAddProject = () => {
    setShowModal(true);
    setEditingProject(null);
    setNewProject({
      name: '',
      startDate: '',
      endDate: '',
      manager: '',
      emissions: 0
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewProject(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!newProject.name || !newProject.startDate || !newProject.endDate || (editingProject && !newProject.manager)) {
      alert(t('projects.alerts.fillRequiredFields'));
      return;
    }
    
    if (editingProject) {
      // Update existing project via API
      try {
        setIsSubmitting(true);
        if (!user?.token) {
          console.error('No authentication token available');
          alert(t('projects.alerts.authRequiredUpdate'));
          return;
        }

        console.log(`Attempting to update project ${editingProject.id}...`);
        const response = await fetch(`${API_URL}/api/projects/${editingProject.id}`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${user.token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            name: newProject.name,
            manager: newProject.manager,
            emissions: parseFloat(newProject.emissions) || 0,
            timeline: {
              startDate: newProject.startDate,
              endDate: newProject.endDate
            }
          })
        });

        console.log(`Update response status: ${response.status}`);

        if (!response.ok) {
          let errorMessage = 'Failed to update project';
          try {
            const errorData = await response.json();
            errorMessage = errorData.message || errorData.error || errorMessage;
            console.error('Update project error details:', errorData);
          } catch (parseError) {
            console.error('Failed to parse error response:', parseError);
            errorMessage = `HTTP ${response.status}: ${response.statusText}`;
          }
          throw new Error(errorMessage);
        }

        const updatedProject = await response.json();
        console.log('Project updated successfully:', updatedProject);
        
        // Update the project in local state
        updateProject(editingProject.id, {
          ...newProject,
          id: updatedProject.id,
          createdAt: updatedProject.createdAt
        });
        
        setShowModal(false);
        setEditingProject(null);
      } catch (error) {
        console.error('Error updating project:', error);
        alert(`Failed to update project: ${error.message}`);
      } finally {
        setIsSubmitting(false);
      }
    } else {
      // Add new project via API
      try {
        setIsSubmitting(true);
        if (!user?.token) {
          console.error('No authentication token available');
          alert(t('projects.alerts.authRequiredCreate'));
          return;
        }

          const response = await fetch(`${API_URL}/api/projects`, {
           method: 'POST',
           headers: {
             'Authorization': `Bearer ${user.token}`,
             'Content-Type': 'application/json',
           },
           body: JSON.stringify({
             name: newProject.name,
             manager: user?.name || user?.email || 'User',
             timeline: {
               startDate: newProject.startDate,
               endDate: newProject.endDate
             }
           })
         });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || 'Failed to create project');
        }

        const createdProject = await response.json();
        console.log('Project created successfully:', createdProject);
        
                 // Add the created project to local state
         addProject({
           ...newProject,
           id: createdProject.id,
           createdAt: createdProject.createdAt,
           manager: user?.name || user?.email || 'User',
           emissions: 0,
           progress: 0
         });
        
        setShowModal(false);
        setEditingProject(null);
      } catch (error) {
        console.error('Error creating project:', error);
        alert(`Failed to create project: ${error.message}`);
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const handleCancel = () => {
    setShowModal(false);
    setEditingProject(null);
  };

  const handleEdit = (project) => {
    setEditingProject(project);
    setNewProject({
      name: project.name,
      startDate: project.startDate,
      endDate: project.endDate,
      manager: project.manager,
      emissions: project.emissions
    });
    setShowModal(true);
  };

  const handleDelete = async (projectId) => {
    if (window.confirm(t('projects.confirmations.deleteSingle'))) {
      try {
        if (!user?.token) {
          console.error('No authentication token available');
          alert(t('projects.alerts.authRequiredDelete'));
          return;
        }

        console.log(`Attempting to delete project ${projectId}...`);
        const response = await fetch(`${API_URL}/api/projects/${projectId}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${user.token}`,
            'Content-Type': 'application/json',
          },
        });
        
        console.log(`Delete response status: ${response.status}`);
        console.log(`Delete response headers:`, Object.fromEntries(response.headers.entries()));

        if (!response.ok) {
          let errorMessage = 'Failed to delete project';
          try {
            const errorData = await response.json();
            errorMessage = errorData.message || errorData.error || errorMessage;
            console.error('Delete project error details:', errorData);
          } catch (parseError) {
            console.error('Failed to parse error response:', parseError);
            errorMessage = `HTTP ${response.status}: ${response.statusText}`;
          }
          throw new Error(errorMessage);
        }

        console.log('Project deleted successfully:', projectId);
        
        // Remove the project from local state
        deleteProject(projectId);
      } catch (error) {
        console.error('Error deleting project:', error);
        alert(`Failed to delete project: ${error.message}`);
      }
    }
  };



  const filteredProjects = projects.filter(project => {
    const matchesSearch = project.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         project.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         project.manager.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || project.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  // Pagination logic
  const totalPages = Math.ceil(filteredProjects.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentItems = filteredProjects.slice(startIndex, endIndex);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  };

  return (
    <div>
      {/* Page Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">{t('projects.title')}</h1>
        <p className="text-gray-600 mt-2">{t('projects.description')}</p>
      </div>

      {/* Controls */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
        <div className="flex flex-col sm:flex-row gap-4 flex-1">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder={t('projects.search.placeholder')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* Status Filter */}
          <div className="relative">
            <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="pl-10 pr-8 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none bg-white"
            >
              <option value="all">{t('projects.filters.allStatus')}</option>
              <option value="planning">{t('projects.filters.planning')}</option>
              <option value="active">{t('projects.filters.active')}</option>
              <option value="completed">{t('projects.filters.completed')}</option>
              <option value="on-hold">{t('projects.filters.onHold')}</option>
            </select>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-2">
          <button
            onClick={handleAddProject}
            className="px-4 py-2 bg-blue-primary text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            {t('projects.actions.addProject')}
          </button>
        </div>
      </div>

      {/* Loading State (skeletons) */}
      {loading && (
        <div className="bg-gray-50 rounded-lg border border-gray-200 overflow-hidden mb-6">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-100 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left">
                    <div className="w-4 h-4 bg-gray-200 rounded-sm animate-pulse" />
                  </th>
                  <th className="px-6 py-3 text-left">
                    <div className="h-4 bg-gray-200 rounded w-40 animate-pulse" />
                  </th>
                  <th className="px-6 py-3 text-left">
                    <div className="h-4 bg-gray-200 rounded w-28 animate-pulse" />
                  </th>
                  <th className="px-6 py-3 text-left">
                    <div className="h-4 bg-gray-200 rounded w-24 animate-pulse" />
                  </th>
                  <th className="px-6 py-3 text-left">
                    <div className="h-4 bg-gray-200 rounded w-32 animate-pulse" />
                  </th>
                  <th className="px-6 py-3 text-right">
                    <div className="h-4 bg-gray-200 rounded w-16 ml-auto animate-pulse" />
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {Array.from({ length: itemsPerPage }).map((_, idx) => (
                  <tr key={idx} className="animate-pulse">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="w-4 h-4 bg-gray-200 rounded-sm" />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="h-4 bg-gray-200 rounded w-40 mb-2" />
                        <div className="h-3 bg-gray-200 rounded w-56" />
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="h-4 bg-gray-200 rounded w-24" />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="h-4 bg-gray-200 rounded w-16" />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="h-4 bg-gray-200 rounded w-28" />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <div className="flex space-x-2 justify-end">
                        <div className="w-4 h-4 bg-gray-200 rounded" />
                        <div className="w-4 h-4 bg-gray-200 rounded" />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {/* Pagination placeholder hidden during loading */}
        </div>
      )}

      {/* Error State */}
      {error && !loading && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">{t('projects.error.title')}</h3>
              <p className="text-sm text-red-700 mt-1">{error}</p>
              <button
                onClick={fetchProjects}
                className="mt-2 text-sm text-red-800 hover:text-red-900 underline"
              >
                {t('projects.error.tryAgain')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Table Section (hide skeletons if loading) */}
      {!error && filteredProjects.length > 0 && !loading && (
        <div className="bg-gray-50 rounded-lg border border-gray-200 overflow-hidden">
        {/* Bulk Actions */}
        {selectedRows.length > 0 && (
          <div className="bg-white px-6 py-3 border-b border-gray-200 flex items-center justify-between">
            <div className="text-sm text-gray-700">
              {t('projects.bulkActions.selected', { count: selectedRows.length })}
            </div>
            <button
              onClick={handleDeleteSelected}
              className="px-3 py-1 text-sm font-medium text-red-600 hover:text-red-800 bg-red-50 hover:bg-red-100 rounded-md transition-colors duration-200 flex items-center space-x-1"
            >
              <Trash2 className="w-4 h-4" />
              <span>{t('projects.bulkActions.deleteSelected')}</span>
            </button>
          </div>
        )}
        
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-100 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left">
                  <input
                    type="checkbox"
                    checked={selectedRows.length === filteredProjects.length && filteredProjects.length > 0}
                    onChange={handleSelectAll}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                </th>
                <th className="px-6 py-3 text-left text-sm font-medium text-gray-700 tracking-wider">
                  {t('projects.table.projectName')}
                </th>
                <th className="px-6 py-3 text-left text-sm font-medium text-gray-700 tracking-wider">
                  {t('projects.table.owner', { defaultValue: 'Owner' })}
                </th>
                <th className="px-6 py-3 text-left text-sm font-medium text-gray-700 tracking-wider">
                  {t('projects.table.emissions')}
                </th>
                <th className="px-6 py-3 text-left text-sm font-medium text-gray-700 tracking-wider">
                  {t('projects.table.timeline')}
                </th>
                <th className="px-6 py-3 text-right text-sm font-medium text-gray-700 tracking-wider">
                  
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {currentItems.map((project) => (
                <tr key={project.id} className="hover:bg-gray-50 transition-colors duration-150">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <input
                      type="checkbox"
                      checked={selectedRows.includes(project.id)}
                      onChange={() => handleCheckboxChange(project.id)}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-gray-900">{project.name}</div>
                      <div className="text-sm text-gray-500">{project.description}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {project.manager}
                  </td>
                                   <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                     {typeof project.emissions === 'number' ? project.emissions.toFixed(1) : (parseFloat(project.emissions) || 0).toFixed(1)}
                   </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                    <div>
                      <div>{new Date(project.startDate).toLocaleDateString()}</div>
                      <div>to {new Date(project.endDate).toLocaleDateString()}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 text-right">
                    <div className="flex space-x-2 justify-end">
                      <button
                        onClick={() => handleEdit(project)}
                        className="text-gray-500 hover:text-gray-700 transition-colors duration-200"
                        title={t('common.edit')}
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(project.id)}
                        className="text-gray-500 hover:text-gray-700 transition-colors duration-200"
                        title={t('common.delete')}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination Controls */}
        <div className="bg-white px-6 py-3 border-t border-gray-200 flex items-center justify-between">
          <div className="flex items-center space-x-6">
            <div className="text-sm text-gray-700">
              {t('projects.pagination.showing', { start: startIndex + 1, end: Math.min(endIndex, filteredProjects.length), total: filteredProjects.length })}
            </div>
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-700">{t('projects.pagination.show')}</span>
              <select
                value={itemsPerPage}
                onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
                className="text-sm bg-gray-50 border border-gray-200 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
              <span className="text-sm text-gray-700">{t('projects.pagination.perPage')}</span>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <button 
              className="px-3 py-1 text-sm text-gray-500 hover:text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={currentPage === 1}
              onClick={() => handlePageChange(currentPage - 1)}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <span className="px-3 py-1 text-sm text-gray-700 bg-gray-100 rounded">
              {currentPage} {t('projects.pagination.of')} {totalPages}
            </span>
            <button 
              className="px-3 py-1 text-sm text-gray-500 hover:text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={currentPage === totalPages}
              onClick={() => handlePageChange(currentPage + 1)}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        </div>
      </div>
      )}

      {/* Empty State */}
      {!loading && !error && filteredProjects.length === 0 && (
        <div className="text-center py-12">
          <div className="text-gray-400 mb-4">
            <FolderOpen className="w-16 h-16 mx-auto" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">{t('projects.emptyState.title')}</h3>
          <p className="text-gray-600 mb-4">
            {searchQuery || statusFilter !== 'all' 
              ? t('projects.emptyState.searchFilterMessage')
              : t('projects.emptyState.noProjectsMessage')
            }
          </p>
          {!searchQuery && statusFilter === 'all' && (
            <button
              onClick={handleAddProject}
              className="px-4 py-2 bg-blue-primary text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2 mx-auto"
            >
              <Plus className="w-4 h-4" />
              {t('projects.actions.addProject')}
            </button>
          )}
        </div>
      )}

      {/* Add/Edit Project Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-6 border w-4/5 max-w-4xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-xl font-medium text-gray-900 mb-6">
                {editingProject ? t('projects.modal.editTitle') : t('projects.modal.addTitle')}
              </h3>
              <form onSubmit={handleSubmit}>
                {/* First Row - Project Name */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('projects.modal.projectName')}</label>
                  <input
                    type="text"
                    name="name"
                    value={newProject.name}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>

                {/* Second Row removed: Description field intentionally omitted */}

                {/* Third Row - Start Date and End Date */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">{t('projects.modal.startDate')}</label>
                    <input
                      type="date"
                      name="startDate"
                      value={newProject.startDate}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">{t('projects.modal.endDate')}</label>
                    <input
                      type="date"
                      name="endDate"
                      value={newProject.endDate}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>
                </div>

                {editingProject && (
                  <>
                    {/* Fourth Row - Project Manager */}
                    <div className="mb-6">
                      <label className="block text-sm font-medium text-gray-700 mb-2">{t('projects.modal.projectManager')}</label>
                      <input
                        type="text"
                        name="manager"
                        value={newProject.manager}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        required
                      />
                    </div>

                    {/* Fifth Row - Emissions */}
                    <div className="mb-6">
                      <label className="block text-sm font-medium text-gray-700 mb-2">{t('projects.modal.emissions')}</label>
                      <input
                        type="number"
                        name="emissions"
                        value={newProject.emissions}
                        onChange={handleInputChange}
                        step="0.1"
                        min="0"
                        className="w-full md:w-1/2 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </>
                )}

                {/* Action Buttons */}
                <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={handleCancel}
                    className="px-6 py-2 text-sm font-medium text-gray-700 bg-gray-200 hover:bg-gray-300 rounded-md transition-colors duration-200 disabled:opacity-50"
                    disabled={isSubmitting}
                  >
                    {t('common.cancel')}
                  </button>
                  <button
                    type="submit"
                    className="px-6 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                    disabled={isSubmitting}
                  >
                    {isSubmitting && (
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                    )}
                    {editingProject ? t('projects.modal.updateProject') : t('projects.modal.addProject')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Projects;
